import { useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { getAuthInfo } from '~/utils/authStorage';
import AuthService from '~/services/AuthService';
import { auth, providerMap } from '~/services/firebase';
import useAuthStore from '~/store/useAuthStore';

const useInitializeAuth = () => {
  const {
    setAuthLoading,
    setAuthInitialized,
    setIdToken,
    setAuthProvider,
    setLoginMethod,
  } = useAuthStore();

  // 초기화 상태 추적
  const initializationRef = useRef({
    firebaseChecked: false,
    cognitoChecked: false,
    completed: false,
  });

  useEffect(() => {
    let firebaseUnsubscribe: (() => void) | undefined;
    let isMounted = true;

    // 초기화 완료 처리 함수
    const finalizeInitialization = () => {
      const { firebaseChecked, cognitoChecked, completed } =
        initializationRef.current;

      // 두 인증 시스템 모두 확인했고 아직 초기화가 완료되지 않은 경우
      if (firebaseChecked && cognitoChecked && !completed && isMounted) {
        initializationRef.current.completed = true;
        setAuthInitialized(true);
        setAuthLoading(false);
      }
    };

    // 인증 초기화 메인 함수
    const initializeAuth = async () => {
      setAuthLoading(true);

      try {
        // 마지막 로그인 정보 가져오기
        const lastAuthInfo = getAuthInfo();

        // 마지막 로그인 방식에 따라 우선순위 결정
        const checkFirebaseFirst =
          !lastAuthInfo || lastAuthInfo.lastAuthProvider === 'firebase';

        if (checkFirebaseFirst) {
          // Firebase 인증 먼저 확인
          await initializeFirebaseAuth();

          // Firebase 인증 실패 시 Cognito 확인
          if (!useAuthStore.getState().isAuthenticated) {
            await initializeCognitoAuth();
          } else {
            // Firebase 인증 성공 시 Cognito 확인 건너뛰기
            initializationRef.current.cognitoChecked = true;
          }
        } else {
          // Cognito 인증 먼저 확인
          await initializeCognitoAuth();

          // Cognito 인증 실패 시 Firebase 확인
          if (!useAuthStore.getState().isAuthenticated) {
            await initializeFirebaseAuth();
          } else {
            // Cognito 인증 성공 시 Firebase 확인 건너뛰기
            initializationRef.current.firebaseChecked = true;
          }
        }

        finalizeInitialization();
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          setAuthInitialized(true);
          setAuthLoading(false);
        }
      }
    };

    // Firebase 인증 초기화
    const initializeFirebaseAuth = async () => {
      return new Promise<void>((resolve) => {
        // Firebase 인증 상태 변경 리스너 설정
        firebaseUnsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!isMounted) {
            resolve();
            return;
          }

          try {
            if (user) {
              const idToken = await user.getIdToken();
              setIdToken(idToken);
              setAuthProvider('firebase');
              const provider = user.providerData[0].providerId;
              setLoginMethod(providerMap[provider]);
              await AuthService.verifySignIn();
            }
          } catch (error) {
            console.error('Firebase auth error:', error);
          } finally {
            initializationRef.current.firebaseChecked = true;
            finalizeInitialization();
            resolve();
          }
        });
      });
    };

    // Cognito 인증 초기화
    const initializeCognitoAuth = async () => {
      try {
        const getCognitoUserInfo = async () => {
          try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens?.idToken;

            if (idToken && idToken.payload.email) {
              setIdToken(idToken.toString());
              setAuthProvider('cognito');
              setLoginMethod('email');

              await AuthService.verifySignIn();
            }
          } catch (error) {
            console.error('Cognito session error:', error);
          }
        };

        await getCognitoUserInfo();
      } finally {
        initializationRef.current.cognitoChecked = true;
        finalizeInitialization();
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      if (firebaseUnsubscribe) firebaseUnsubscribe();
    };
  }, []);
};

export default useInitializeAuth;
